/**
 * Smart Recovery™ Tab — combined visual dashboard for fisio/osteo tenants.
 *
 * Sections:
 *   1. KPI Hero (active issues, latest pain, active packages, remaining sessions)
 *   2. Pain Score Sparkline (premium chart, add new score inline)
 *   3. Recovery Timeline (visual events: body map updates, pain scores, sessions)
 *
 * Pacchetti Sedute have their own dedicated tab (SessionPackages.jsx) — this
 * file shows just the summary KPI + recent activity.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Activity, Flame, TrendingDown, TrendingUp, Plus, Loader2,
  Heart, Sparkles, Layers, Calendar, FileText, AlertCircle,
  CheckCircle2, Package, Stethoscope, X, Award,
} from "lucide-react";

const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

const EVENT_META = {
  body_map_update:  { icon: Activity,    color: "from-cyan-500 to-blue-500",    label: "Mappa corporea" },
  pain_score:       { icon: Flame,       color: "from-rose-500 to-orange-500",  label: "Dolore registrato" },
  package_created:  { icon: Package,     color: "from-violet-500 to-pink-500",  label: "Pacchetto creato" },
  package_consumed: { icon: CheckCircle2,color: "from-emerald-500 to-teal-500", label: "Seduta effettuata" },
  exercise_assigned:{ icon: Sparkles,    color: "from-amber-500 to-orange-500", label: "Esercizio" },
  posture_photo:    { icon: FileText,    color: "from-blue-500 to-indigo-500",  label: "Foto postura" },
  consent_signed:   { icon: FileText,    color: "from-violet-500 to-blue-500",  label: "Consenso firmato" },
  treatment_note:   { icon: Stethoscope, color: "from-slate-500 to-slate-700",  label: "Nota clinica" },
  recovery_milestone: { icon: Award,     color: "from-emerald-400 to-teal-500", label: "Milestone" },
};

const RecoveryTab = ({ clientId }) => {
  const [summary,  setSummary]  = useState(null);
  const [pain,     setPain]     = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loading,  setLoading]  = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p, t] = await Promise.all([
        axios.get(`${API}/wellness/summary/${clientId}`,      { withCredentials: true }),
        axios.get(`${API}/wellness/pain-scores/${clientId}`,  { withCredentials: true }),
        axios.get(`${API}/wellness/timeline/${clientId}`,     { withCredentials: true }),
      ]);
      setSummary(s.data);
      setPain(p.data);
      setTimeline(t.data.events || []);
    } catch {
      toast.error("Errore caricamento dashboard recupero");
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  if (loading && !summary) {
    return (
      <div className="space-y-4" data-testid="recovery-tab-loading">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[0,1,2,3].map(i => <div key={i} className="h-24 rounded-2xl bg-slate-100 dark:bg-white/5 animate-pulse" />)}
        </div>
        <div className="h-40 rounded-2xl bg-slate-100 dark:bg-white/5 animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="recovery-tab">
      <KpiHero summary={summary} pain={pain} />
      <PainSparkline pain={pain} clientId={clientId} onChanged={load} />
      <Timeline events={timeline} />
    </div>
  );
};

/* ============================================================ */
/* KPI HERO                                                     */
/* ============================================================ */
const KpiHero = ({ summary, pain }) => {
  const s = summary || {};
  const painDelta = (s.pain_initial != null && s.pain_latest != null) ? (s.pain_initial - s.pain_latest) : null;
  const trend = painDelta == null ? null : painDelta > 0 ? "down" : painDelta < 0 ? "up" : "flat";
  return (
    <div className="rounded-3xl border border-slate-200/60 dark:border-white/10 bg-gradient-to-br from-cyan-500/5 via-transparent to-emerald-500/5 dark:from-cyan-500/10 dark:to-emerald-500/10 p-5">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-2xl bg-gradient-to-br from-cyan-500 to-emerald-500 flex items-center justify-center shadow">
          <Heart className="w-4 h-4 text-white" />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-600 dark:text-cyan-400">Smart Recovery</p>
          <h3 className="text-lg sm:text-xl font-black text-slate-900 dark:text-white tracking-tight">Dashboard clinica</h3>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
        <Kpi
          icon={AlertCircle} label="Aree attive"
          value={s.active_issues ?? 0}
          accent={s.active_issues > 0 ? "amber" : "emerald"}
          testid="kpi-active-issues"
        />
        <Kpi
          icon={Flame} label="Dolore attuale"
          value={s.pain_latest != null ? `${s.pain_latest}/10` : "—"}
          accent={s.pain_latest >= 6 ? "rose" : s.pain_latest >= 3 ? "amber" : "emerald"}
          testid="kpi-pain-latest"
          delta={painDelta != null
            ? <span className={`inline-flex items-center gap-0.5 text-[10px] font-bold ${trend === "down" ? "text-emerald-500" : "text-rose-500"}`}>
                {trend === "down" ? <TrendingDown className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />}
                {Math.abs(painDelta)}
              </span>
            : null}
        />
        <Kpi
          icon={Package} label="Pacchetti attivi"
          value={s.active_packages ?? 0}
          accent="violet"
          testid="kpi-active-packages"
        />
        <Kpi
          icon={Calendar} label="Sedute rimaste"
          value={s.remaining_sessions ?? 0}
          accent="cyan"
          testid="kpi-remaining-sessions"
        />
      </div>
    </div>
  );
};

const ACCENT = {
  emerald: "from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 text-emerald-600 dark:text-emerald-300",
  amber:   "from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 text-amber-600 dark:text-amber-300",
  rose:    "from-rose-50 to-pink-50 dark:from-rose-900/20 dark:to-pink-900/20 text-rose-600 dark:text-rose-300",
  violet:  "from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20 text-violet-600 dark:text-violet-300",
  cyan:    "from-cyan-50 to-sky-50 dark:from-cyan-900/20 dark:to-sky-900/20 text-cyan-600 dark:text-cyan-300",
};

const Kpi = ({ icon: Icon, label, value, accent = "cyan", testid, delta }) => (
  <div className={`rounded-2xl border border-slate-200/60 dark:border-white/10 bg-gradient-to-br ${ACCENT[accent]} p-3`} data-testid={testid}>
    <div className="flex items-center justify-between">
      <Icon className="w-3.5 h-3.5 opacity-80" />
      {delta}
    </div>
    <p className="text-[10px] uppercase tracking-wider font-bold opacity-80 mt-1">{label}</p>
    <p className="font-black text-slate-900 dark:text-white text-xl mt-0.5 font-variant-numeric tabular-nums">{value}</p>
  </div>
);

/* ============================================================ */
/* PAIN SPARKLINE                                               */
/* ============================================================ */
const PainSparkline = ({ pain, clientId, onChanged }) => {
  const [adding, setAdding] = useState(false);
  const [score,  setScore]  = useState(5);
  const [open,   setOpen]   = useState(false);

  const submit = async () => {
    setAdding(true);
    try {
      await axios.post(`${API}/wellness/pain-scores/${clientId}`, { score }, { withCredentials: true });
      toast.success(`Dolore ${score}/10 registrato`);
      setOpen(false);
      setScore(5);
      onChanged();
    } catch {
      toast.error("Errore registrazione");
    } finally {
      setAdding(false);
    }
  };

  const scores = useMemo(
    () => [...(pain?.scores || [])].reverse(),  // oldest first for chart
    [pain]
  );

  return (
    <div className="rounded-3xl border border-slate-200/60 dark:border-white/10 bg-white dark:bg-slate-900/40 p-5" data-testid="pain-sparkline-card">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-rose-500">Pain tracking</p>
          <h4 className="text-base sm:text-lg font-black text-slate-900 dark:text-white tracking-tight">Evoluzione dolore</h4>
        </div>
        <div className="flex items-center gap-2 text-xs">
          {pain?.initial != null && (
            <span className="font-bold text-slate-400">
              Iniziale <span className="text-slate-700 dark:text-slate-200">{pain.initial}</span>
            </span>
          )}
          {pain?.latest != null && pain?.initial != null && (
            <span className="text-slate-300">→</span>
          )}
          {pain?.latest != null && (
            <span className="font-bold text-slate-400">
              Attuale <span className={`${pain.latest >= 6 ? "text-rose-500" : pain.latest >= 3 ? "text-amber-500" : "text-emerald-500"}`}>{pain.latest}</span>
            </span>
          )}
          <Button
            size="sm"
            onClick={() => setOpen(o => !o)}
            className="h-7 rounded-full bg-gradient-to-r from-rose-500 to-orange-500 text-white text-[11px]"
            data-testid="pain-add-btn"
          >
            <Plus className="w-3 h-3 mr-1" /> Registra
          </Button>
        </div>
      </div>

      {open && (
        <div className="rounded-2xl bg-slate-50 dark:bg-white/5 border border-slate-200/60 dark:border-white/10 p-3 mb-3" data-testid="pain-add-panel">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Livello dolore</span>
            <span className="text-2xl font-black text-slate-900 dark:text-white font-variant-numeric tabular-nums">{score}<span className="text-slate-400 text-sm font-normal">/10</span></span>
          </div>
          <input
            type="range" min="0" max="10" value={score}
            onChange={e => setScore(Number(e.target.value))}
            className="w-full accent-rose-500"
            data-testid="pain-add-slider"
          />
          <div className="flex justify-between text-[9px] text-slate-400 mt-0.5">
            <span>Nessuno</span><span>Moderato</span><span>Insopportabile</span>
          </div>
          <div className="flex gap-2 mt-3">
            <Button size="sm" variant="outline" onClick={() => setOpen(false)} className="rounded-full text-xs">Annulla</Button>
            <Button size="sm" onClick={submit} disabled={adding}
              className="flex-1 rounded-full bg-gradient-to-r from-rose-500 to-orange-500 text-white text-xs"
              data-testid="pain-add-confirm">
              {adding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Conferma"}
            </Button>
          </div>
        </div>
      )}

      {scores.length === 0 ? (
        <p className="text-xs text-slate-400 text-center py-6">Nessuna misurazione ancora. Registra il primo livello di dolore.</p>
      ) : (
        <Chart scores={scores} />
      )}
    </div>
  );
};

const Chart = ({ scores }) => {
  const W = 600, H = 120, P = 10;
  const xs = scores.map((_, i) => P + (i * (W - 2 * P)) / Math.max(1, scores.length - 1));
  const ys = scores.map(s => P + (s.score / 10) * (H - 2 * P));
  const yInv = ys.map(y => H - y + P);
  const points = xs.map((x, i) => `${x},${yInv[i]}`).join(" ");
  const path = `M${points.split(" ").join(" L")}`;
  const lastScore = scores[scores.length - 1]?.score ?? 0;
  const stroke = lastScore >= 6 ? "#ef4444" : lastScore >= 3 ? "#f59e0b" : "#10b981";

  return (
    <svg viewBox={`0 0 ${W} ${H + 20}`} className="w-full h-auto" data-testid="pain-chart">
      <defs>
        <linearGradient id="painGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"  stopColor={stroke} stopOpacity="0.35" />
          <stop offset="100%" stopColor={stroke} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      {/* Grid */}
      {[0, 3, 5, 7, 10].map(g => {
        const y = H - (P + (g / 10) * (H - 2 * P)) + P;
        return (
          <g key={g}>
            <line x1={P} y1={y} x2={W - P} y2={y} stroke="rgb(226 232 240)" strokeDasharray="2,3" strokeOpacity="0.6" />
            <text x={W - P + 2} y={y + 3} fontSize="8" fill="#94a3b8" fontWeight="700">{g}</text>
          </g>
        );
      })}
      {/* Area */}
      <path d={`${path} L${xs[xs.length-1]},${H + P} L${xs[0]},${H + P} Z`} fill="url(#painGrad)" />
      {/* Line */}
      <path d={path} fill="none" stroke={stroke} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
      {/* Dots */}
      {xs.map((x, i) => (
        <g key={i}>
          <circle cx={x} cy={yInv[i]} r="3" fill="#fff" stroke={stroke} strokeWidth="2" />
        </g>
      ))}
      {/* X axis dates */}
      {scores.length <= 8 && scores.map((s, i) => (
        <text key={i} x={xs[i]} y={H + 18} fontSize="7" fill="#94a3b8" textAnchor="middle">
          {new Date(s.measured_at).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" })}
        </text>
      ))}
    </svg>
  );
};

/* ============================================================ */
/* TIMELINE                                                     */
/* ============================================================ */
const Timeline = ({ events }) => {
  if (events.length === 0) {
    return (
      <div className="rounded-3xl border border-dashed border-slate-200 dark:border-white/10 p-8 text-center" data-testid="recovery-timeline-empty">
        <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">Nessun evento ancora</p>
        <p className="text-xs text-slate-400 mt-1">Aggiorna la mappa corporea o registra un dolore per iniziare la timeline.</p>
      </div>
    );
  }
  return (
    <div className="rounded-3xl border border-slate-200/60 dark:border-white/10 bg-white dark:bg-slate-900/40 p-5" data-testid="recovery-timeline">
      <div className="flex items-center gap-2 mb-3">
        <Activity className="w-4 h-4 text-cyan-500" />
        <h4 className="font-black text-slate-900 dark:text-white">Timeline recupero</h4>
        <span className="text-xs text-slate-400 ml-auto">{events.length} eventi</span>
      </div>
      <ul className="relative pl-4 space-y-3 max-h-[420px] overflow-y-auto">
        <span aria-hidden className="absolute left-[7px] top-1 bottom-1 w-0.5 bg-gradient-to-b from-cyan-200 via-violet-200 to-transparent dark:from-cyan-900/40 dark:via-violet-900/30" />
        {events.map(ev => {
          const m = EVENT_META[ev.event_type] || EVENT_META.treatment_note;
          const Icon = m.icon;
          return (
            <li key={ev.event_id} className="relative" data-testid={`timeline-event-${ev.event_type}`}>
              <span className={`absolute -left-[1px] top-1.5 w-3.5 h-3.5 rounded-full bg-gradient-to-br ${m.color} shadow-md ring-4 ring-white dark:ring-slate-900`} />
              <div className="ml-5 rounded-xl bg-slate-50 dark:bg-white/5 border border-slate-200/60 dark:border-white/10 p-2.5">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    <Icon className="w-3 h-3" /> {m.label}
                  </span>
                  <span className="text-[10px] text-slate-400">{fmtDate(ev.occurred_at)}</span>
                </div>
                <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 mt-1">{ev.title}</p>
                {ev.metadata?.score != null && (
                  <span className="inline-block mt-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300">
                    Dolore {ev.metadata.score}/10
                  </span>
                )}
                {ev.metadata?.remaining != null && (
                  <span className="inline-block mt-1 ml-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                    {ev.metadata.remaining} sedute rimaste
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default RecoveryTab;
